import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cx } from "@/shared/lib/cx";
import { IconChip, type IconName } from "../Icon";

type Width = "sm" | "md" | "lg";
const w = (width?: Width) => width && `w-${width}`;

export const Input = ({ className, width, ...p }: InputHTMLAttributes<HTMLInputElement> & { width?: Width }) =>
  <input className={cx("input", w(width), className)} {...p} />;

export const Textarea = ({ className, mono, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }) =>
  <textarea className={cx("input", mono && "mono", className)} {...p} />;

export const Select = ({ className, width, children, ...p }: SelectHTMLAttributes<HTMLSelectElement> & { width?: Width }) =>
  <select className={cx("input", w(width), className)} {...p}>{children}</select>;

/** Number input that reports numbers, not strings. */
export const NumberInput = ({ value, onValueChange, className, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: number; onValueChange: (v: number) => void; width?: Width;
}) => <Input type="number" inputMode="decimal" className={cx("tabular", className)} value={value} onChange={(e) => onValueChange(Number(e.target.value))} {...p} />;

export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

/** On/off toggle for settings that take effect immediately. */
export function Switch({ checked, onChange, label, disabled, hideLabel }: {
  checked: boolean; onChange: (v: boolean) => void; label: ReactNode; disabled?: boolean;
  /** Keep the label for screen readers only. */
  hideLabel?: boolean;
}) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="switch-track" aria-hidden />
      <span className={hideLabel ? "sr-only" : undefined}>{label}</span>
    </label>
  );
}

export interface Option<T extends string = string> { value: T; label: string; disabled?: boolean; icon?: IconName }

/** Typed <select> from an options list, with an optional empty choice. */
export function OptionSelect<T extends string>({ value, options, onChange, placeholder, width, disabled, id, "aria-label": ariaLabel }: {
  value: T | ""; options: Option<T>[]; onChange: (v: T) => void;
  /** Label of the "" option (e.g. "Mọi trạng thái"); omit for a required choice. */
  placeholder?: string; width?: Width; disabled?: boolean; id?: string; "aria-label"?: string;
}) {
  return (
    <Select id={id} value={value} width={width} disabled={disabled} aria-label={ariaLabel ?? placeholder} onChange={(e) => onChange(e.target.value as T)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
    </Select>
  );
}

/** Radio tiles: a visible, one-click choice among a handful of options. */
export function ChoiceGroup<T extends string>({ name, value, options, onChange, label }: {
  name: string; value: T; options: Option<T>[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="choice-grid" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <label key={o.value} className="choice">
          <input type="radio" name={name} value={o.value} checked={value === o.value} disabled={o.disabled} onChange={() => onChange(o.value)} />
          {o.icon && <IconChip name={o.icon} size="sm" />}
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}
